import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { IModelDocument } from '../../../shared/interfaces';
import api from '../../../services/api';
import { schemaDocument } from '../schemas/yup.document.schema';
import {
  AInput,
  ASelect,
  ASwitch,
  ATitle,
  ContainerForm,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { Col, Form, Row, Tag, Tooltip, Space, Button } from 'antd';
import ReactQuill from 'react-quill';
import {
  formatsText,
  modulesText,
} from '../../../shared/utils/propreties-text';
import {
  CopyOutlined,
  FileOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useI18n } from '../../../shared/i18n';
import * as S from './styles';

const ModelDocumentCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useI18n();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [title, setTitle] = useState<string>();
  const [text, setText] = useState<string>();
  const [status, setStatus] = useState<boolean>(false);
  const isPrescriptionRoute = location.pathname.startsWith(
    '/model_prescription',
  );
  const [kind, setKind] = useState<'GENERAL' | 'PRESCRIPTION'>(
    isPrescriptionRoute || searchParams.get('kind') === 'PRESCRIPTION'
      ? 'PRESCRIPTION'
      : 'GENERAL',
  );

  const isPrescription = kind === 'PRESCRIPTION';

  const getListRoute = (value?: 'GENERAL' | 'PRESCRIPTION') =>
    value === 'PRESCRIPTION' ? '/models_prescriptions' : '/models_documents';

  const defaultPrescriptionTemplate = useMemo(
    () =>
      `<p><strong>${t('modelsDocumentsCreateEdit.template.patient')}:</strong> {nome}</p><p><strong>${t('modelsDocumentsCreateEdit.template.date')}:</strong> {dataReceita}</p><p>{itensReceita}</p><p>{orientacoesReceita}</p>`,
    [t],
  );

  const keywords = useMemo(
    () => [
      {
        key: '{nome}',
        description: t('modelsDocumentsCreateEdit.keywords.name'),
      },
      {
        key: '{cpf}',
        description: t('modelsDocumentsCreateEdit.keywords.cpf'),
      },
      {
        key: '{nascimento}',
        description: t('modelsDocumentsCreateEdit.keywords.birthDate'),
      },
      {
        key: '{celular}',
        description: t('modelsDocumentsCreateEdit.keywords.phone'),
      },
      {
        key: '{sexo}',
        description: t('modelsDocumentsCreateEdit.keywords.gender'),
      },
      {
        key: '{estadoCivil}',
        description: t('modelsDocumentsCreateEdit.keywords.maritalStatus'),
      },
      {
        key: '{email}',
        description: t('modelsDocumentsCreateEdit.keywords.email'),
      },
      {
        key: '{nacionalidade}',
        description: t('modelsDocumentsCreateEdit.keywords.nationality'),
      },
      {
        key: '{naturalidade}',
        description: t('modelsDocumentsCreateEdit.keywords.naturalness'),
      },
      {
        key: '{profissao}',
        description: t('modelsDocumentsCreateEdit.keywords.profession'),
      },
      {
        key: '{cep}',
        description: t('modelsDocumentsCreateEdit.keywords.zipCode'),
      },
      {
        key: '{logradouro}',
        description: t('modelsDocumentsCreateEdit.keywords.street'),
      },
      {
        key: '{numero}',
        description: t('modelsDocumentsCreateEdit.keywords.number'),
      },
      {
        key: '{bairro}',
        description: t('modelsDocumentsCreateEdit.keywords.district'),
      },
      {
        key: '{complemento}',
        description: t('modelsDocumentsCreateEdit.keywords.complement'),
      },
      {
        key: '{cidade}',
        description: t('modelsDocumentsCreateEdit.keywords.city'),
      },
      {
        key: '{uf}',
        description: t('modelsDocumentsCreateEdit.keywords.state'),
      },
    ],
    [t],
  );

  const keywordsList = useMemo(
    () => [
      ...keywords,
      ...(isPrescription
        ? [
            {
              key: '{dataReceita}',
              description: t(
                'modelsDocumentsCreateEdit.keywords.prescriptionDate',
              ),
            },
            {
              key: '{itensReceita}',
              description: t(
                'modelsDocumentsCreateEdit.keywords.prescriptionItems',
              ),
            },
            {
              key: '{orientacoesReceita}',
              description: t(
                'modelsDocumentsCreateEdit.keywords.prescriptionGuidelines',
              ),
            },
          ]
        : []),
    ],
    [isPrescription, keywords, t],
  );

  const loadData = useCallback(async () => {
    interface IResponseDocument {
      data: IModelDocument;
    }

    const response = await api.get(`/documents/${id}`);
    const { data }: IResponseDocument = response;
    setTitle(data.title);
    setText(data.text_description);
    setStatus(data.status);
    setKind(data.kind || 'GENERAL');
  }, [id]);

  const handleCreateSubmit = useCallback(async () => {
    schemaDocument
      .validate({
        title,
        kind,
        text_description: text,
      })
      .then(async (validated) => {
        await api
          .post(`/documents/`, validated)
          .then(() => {
            addToast({
              type: 'success',
              title: t('modelsDocumentsCreateEdit.toast.createdTitle'),
              description: t('modelsDocumentsCreateEdit.toast.created'),
            });
            setTimeout(function () {
              navigate(getListRoute(kind));
            }, 1000);
          })
          .catch((error) => {
            const message = error.response.data.message;
            addToast({
              type: 'error',
              title: t('modelsDocumentsCreateEdit.toast.errorTitle'),
              description:
                message ?? t('modelsDocumentsCreateEdit.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('modelsDocumentsCreateEdit.toast.blankFields'),
          description: err.errors[0],
        });
      });
  }, [addToast, kind, navigate, text, title, t]);

  const handleEditSubmit = useCallback(async () => {
    schemaDocument
      .validate({
        title,
        kind,
        text_description: text,
        status,
      })
      .then(async (validated) => {
        await api
          .patch(`/documents/${id}`, validated)
          .then(() => {
            addToast({
              type: 'info',
              title: t('modelsDocumentsCreateEdit.toast.infoTitle'),
              description: t('modelsDocumentsCreateEdit.toast.updated'),
            });
          })
          .catch((error) => {
            const message = error.response.data.message;
            addToast({
              type: 'error',
              title: t('modelsDocumentsCreateEdit.toast.errorTitle'),
              description:
                message ?? t('modelsDocumentsCreateEdit.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('modelsDocumentsCreateEdit.toast.blankFields'),
          description: err.errors[0],
        });
      });
  }, [addToast, id, status, text, title, t]);

  useEffect(() => {
    if (id !== 'new') {
      loadData();
    }
  }, [id, loadData]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);

    if (kind === 'PRESCRIPTION') {
      nextParams.set('kind', 'PRESCRIPTION');
    } else {
      nextParams.delete('kind');
    }

    setSearchParams(nextParams, { replace: true });

    if (id === 'new' && kind === 'PRESCRIPTION' && !text) {
      setText(defaultPrescriptionTemplate);
    }
  }, [id, kind, searchParams, setSearchParams, text]);

  const handleCopyKeyword = (keyword: string) => {
    navigator.clipboard.writeText(keyword);
    addToast({
      type: 'success',
      title: t('modelsDocumentsCreateEdit.toast.copiedTitle'),
      description: t('modelsDocumentsCreateEdit.toast.copiedDescription', {
        keyword,
      }),
    });
  };

  return (
    <PageLayout>
      <PageIntro
        badge={t('modelsDocumentsCreateEdit.intro.badge')}
        icon={<FileOutlined />}
        title={
          isPrescription
            ? id === 'new'
              ? t('modelsDocumentsCreateEdit.intro.createPrescription')
              : t('modelsDocumentsCreateEdit.intro.editPrescription')
            : id === 'new'
              ? t('modelsDocumentsCreateEdit.intro.createDocument')
              : t('modelsDocumentsCreateEdit.intro.editDocument')
        }
        description={
          isPrescription
            ? t('modelsDocumentsCreateEdit.intro.prescriptionDescription')
            : t('modelsDocumentsCreateEdit.intro.documentDescription')
        }
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '90%', marginTop: 24 }}>
            {id !== 'new' && (
              <S.StatusBadge
                count={
                  <Tag color={status ? 'green' : 'red'}>
                    {status
                      ? t('modelsDocumentsCreateEdit.status.active')
                      : t('modelsDocumentsCreateEdit.status.inactive')}
                  </Tag>
                }
              />
            )}

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">📝</span>
                {t('modelsDocumentsCreateEdit.sections.basicInfo')}
              </S.FormSectionTitle>
              <Row gutter={16}>
                <Col span={16}>
                  <Form.Item
                    label={
                      isPrescription
                        ? t(
                            'modelsDocumentsCreateEdit.fields.prescriptionTitle',
                          )
                        : t('modelsDocumentsCreateEdit.fields.documentTitle')
                    }
                    required
                    tooltip={t('modelsDocumentsCreateEdit.tooltips.modelName')}
                  >
                    <Tooltip
                      title={t(
                        'modelsDocumentsCreateEdit.tooltips.modelNameDescription',
                      )}
                    >
                      <AInput
                        value={title}
                        placeholder={t(
                          'modelsDocumentsCreateEdit.placeholders.titleExample',
                        )}
                        onChange={(e) => setTitle(e.target.value)}
                        maxLength={100}
                      />
                    </Tooltip>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t('modelsDocumentsCreateEdit.fields.modelType')}
                    required
                  >
                    <ASelect
                      value={kind}
                      onChange={(value) =>
                        setKind(value as 'GENERAL' | 'PRESCRIPTION')
                      }
                      options={[
                        {
                          label: t(
                            'modelsDocumentsCreateEdit.options.document',
                          ),
                          value: 'GENERAL',
                        },
                        {
                          label: t(
                            'modelsDocumentsCreateEdit.options.prescription',
                          ),
                          value: 'PRESCRIPTION',
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">🔑</span>
                {t('modelsDocumentsCreateEdit.sections.keywords')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t('modelsDocumentsCreateEdit.instructions.keywords')}
                </p>
              </S.InstructionBox>

              <Space direction="vertical" style={{ width: '100%' }} size={0}>
                {keywordsList.map((item, index) => (
                  <S.KeywordItem key={`keyword-${index}`}>
                    <span className="keyword-key">{item.key}</span>
                    <span className="keyword-desc">{item.description}</span>
                    <Tooltip
                      title={t(
                        'modelsDocumentsCreateEdit.tooltips.copyKeyword',
                      )}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyKeyword(item.key)}
                        className="copy-btn"
                      />
                    </Tooltip>
                  </S.KeywordItem>
                ))}
              </Space>

              <div
                style={{
                  marginTop: 16,
                  padding: '12px 12px',
                  backgroundColor: '#fffbe6',
                  borderLeft: '3px solid #faad14',
                  borderRadius: '4px',
                }}
              >
                <strong style={{ color: '#ad6800' }}>
                  ⚠️{' '}
                  {t('modelsDocumentsCreateEdit.instructions.importantTitle')}
                </strong>
                <p
                  style={{
                    marginBottom: 0,
                    marginTop: 4,
                    color: '#ad6800',
                    fontSize: 13,
                  }}
                >
                  {t(
                    'modelsDocumentsCreateEdit.instructions.importantDescription',
                  )}{' '}
                  (com as chaves{' '}
                  <code
                    style={{
                      backgroundColor: '#fff7e6',
                      padding: '2px 4px',
                      borderRadius: '2px',
                    }}
                  >
                    {'{'} {'}'}
                  </code>{' '}
                  e sem espaços).
                </p>
              </div>
            </S.StyledCard>

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">📄</span>
                {t('modelsDocumentsCreateEdit.sections.content')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t('modelsDocumentsCreateEdit.instructions.content')}
                </p>
              </S.InstructionBox>

              <Row>
                <Col span={24}>
                  <Form.Item
                    label={t('modelsDocumentsCreateEdit.fields.text')}
                    required
                  >
                    <ReactQuill
                      theme="snow"
                      value={text}
                      onChange={setText}
                      modules={modulesText}
                      formats={formatsText}
                      style={{ height: '35vh', marginBottom: 20 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            {id !== 'new' && (
              <S.StyledCard>
                <S.FormSectionTitle>
                  <span className="icon">✅</span>
                  {t('modelsDocumentsCreateEdit.sections.modelStatus')}
                </S.FormSectionTitle>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      label={t(
                        'modelsDocumentsCreateEdit.fields.activateModel',
                      )}
                      tooltip={t(
                        'modelsDocumentsCreateEdit.tooltips.deactivateModel',
                      )}
                    >
                      <ASwitch
                        checked={status}
                        checkedChildren={t(
                          'modelsDocumentsCreateEdit.status.active',
                        )}
                        unCheckedChildren={t(
                          'modelsDocumentsCreateEdit.status.inactive',
                        )}
                        onChange={() => {
                          setStatus(!status);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </S.StyledCard>
            )}

            <OptionFooter
              onClickCancel={() => navigate(getListRoute(kind))}
              onClickOk={id !== 'new' ? handleEditSubmit : handleCreateSubmit}
            />
          </Form>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default ModelDocumentCreateEdit;
